import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import {
  ListingDetails,
  PersonalizedSearchForm,
  PropertyDetails,
} from '@keaze/web/common/interfaces';
import {
  BedroomFilterType,
  OnBedroomFilterClick,
} from './bedroomFilters.types';
import { GTMViewListing } from '@keaze/web/libs/GTM';
import { useLocalStorage, usePrevious } from '@keaze/web/hooks';
import { LocalStorageKeys, QueryParams } from '@keaze/web/common/enums';
import { DEFAULT_PERSONALIZED_SEARCH_DATA } from '@keaze/web/utils/defaultValues';
import { replaceRouteQuery } from '@keaze/web/utils/replaceRouteQuery';

type UseBedroomFiltersExpected = {
  bedroomFilters: BedroomFilterType[];
  onBedroomFilterClick: OnBedroomFilterClick;
};

type UseBedroomFiltersArgs = {
  listing: ListingDetails;
  bedrooms: number[];
  properties: PropertyDetails[];
};

type UseBedroomFilters = (
  args: UseBedroomFiltersArgs
) => UseBedroomFiltersExpected;

type GenerateBedroomsPrices = (
  bedroomCount: number,
  properties: PropertyDetails[]
) => number[];

type GenerateBedroomFiltersArgs = {
  bedrooms: number[];
  properties: PropertyDetails[];
  bedroomsFromQuery: number[];
};

type GenerateBedroomFilters = (
  args: GenerateBedroomFiltersArgs
) => BedroomFilterType[];

type GetBedroomsFromQuery = (bedrooms?: string | string[]) => number[];

type isBedroomExist = (
  bedrooms: number[],
  currentBedroomCount: number
) => boolean;

const generateBedroomsPrices: GenerateBedroomsPrices = (
  bedroomCount,
  properties
) =>
  properties.reduce<number[]>(
    (
      acc,
      { bedrooms, fullMarketPrice, monthlyRent, forSale, minShareValue }
    ) => {
      if (bedrooms === bedroomCount) {
        const forSalePrice = minShareValue ?? fullMarketPrice;
        const price = forSale ? forSalePrice : monthlyRent;

        return [...acc, price === null ? -1 : price];
      }

      return acc;
    },
    []
  );

const generateBedroomFilters: GenerateBedroomFilters = ({
  bedrooms,
  properties,
  bedroomsFromQuery,
}) =>
  bedrooms.reduce<BedroomFilterType[]>((acc, bedroomCount) => {
    const bedroomsPrices = generateBedroomsPrices(bedroomCount, properties);
    const minPrice = Math.min(...bedroomsPrices);
    const bedroom = bedroomCount === 0 ? 'Studio' : `${bedroomCount} bedroom`;

    return [
      ...acc,
      {
        price: minPrice,
        count: bedroomCount,
        bedroom,
        checked: isBedroomExist(bedroomsFromQuery, bedroomCount),
      },
    ];
  }, []);

export const getBedroomsFromQuery: GetBedroomsFromQuery = (bedrooms) => {
  if (bedrooms) {
    if (Array.isArray(bedrooms)) {
      return bedrooms.map((bedroom) => Number(bedroom));
    }

    return [Number(bedrooms)];
  }

  return [];
};

export const isBedroomExist: isBedroomExist = (
  bedrooms,
  currentBedroomCount
) => {
  const bedroom = bedrooms.find(
    (bedroomCount) => bedroomCount === currentBedroomCount
  );

  if (bedroom === 0) {
    return true;
  }

  return !!bedroom;
};

export const useBedroomFilters: UseBedroomFilters = ({
  listing,
  bedrooms,
  properties,
}) => {
  const router = useRouter();
  const bedroomsQuery = router.query[QueryParams.Bedrooms];
  const bedroomsFromQuery = useMemo(() => getBedroomsFromQuery(bedroomsQuery), [
    bedroomsQuery,
  ]);
  const prevBedroomsFromQuery = usePrevious(bedroomsFromQuery, []);
  const [bedroomFilters, setBedroomFilters] = useState<BedroomFilterType[]>(
    generateBedroomFilters({
      bedrooms,
      properties,
      bedroomsFromQuery,
    })
  );
  const [
    { householdIncome, estimatedDeposit },
  ] = useLocalStorage<PersonalizedSearchForm>(
    LocalStorageKeys.personalizedSearch,
    DEFAULT_PERSONALIZED_SEARCH_DATA
  );

  useEffect(() => {
    if (bedroomsFromQuery.length !== prevBedroomsFromQuery.length) {
      GTMViewListing({
        event: 'view_listing_filter_bedrooms',
        listingSchemes: listing.schemes,
        listingSlug: listing.slug,
        bedroomsFilter: bedroomsFromQuery,
        householdIncome,
        estimatedDeposit,
      });

      setBedroomFilters(
        generateBedroomFilters({
          bedrooms,
          properties,
          bedroomsFromQuery,
        })
      );
    }
  }, [
    listing,
    bedrooms,
    properties,
    estimatedDeposit,
    householdIncome,
    bedroomsFromQuery,
    prevBedroomsFromQuery,
  ]);

  const handleBedroomFilterClick: OnBedroomFilterClick = useCallback(
    (currentBedroomCount) => {
      const isSelectedBedroom = isBedroomExist(
        bedroomsFromQuery,
        currentBedroomCount
      );

      if (isSelectedBedroom) {
        return replaceRouteQuery(router, {
          ...router.query,
          bedrooms: bedroomsFromQuery.filter(
            (bedroomCount) => bedroomCount !== currentBedroomCount
          ),
        });
      }

      return replaceRouteQuery(router, {
        ...router.query,
        bedrooms: [...bedroomsFromQuery, currentBedroomCount],
      });
    },
    [router, bedroomsFromQuery]
  );

  return {
    bedroomFilters,
    onBedroomFilterClick: handleBedroomFilterClick,
  };
};
